import { LoanDocumentStatus, OrderStatus } from '@app/shared/models';

export type OrderStatusValue = keyof typeof OrderStatus;

export type GetOrdersRequest = {
  page: number;
  pageSize: number;
  filters?: string;
  sorts?: string;
};

export type CreateOrdersByOnboardingFileRequest = {
  name: string;
  outgoingServicer: string;
  onboardingFile: File;
  channelName?: string | null;
  reportsOutputDirectory?: string | null;
};

export enum ProcessingSourceEnum {
  Both = 'Both',
  OneDilligence = 'OneDilligence',
  GenAI = 'GenAI'
}

export enum ProcessingCheckpointEnum {
  Unknown = 'Unknown',
  CompleteOnboarding = 'CompleteOnboarding',
  CompleteIndexing = 'CompleteIndexing',
  CompleteSplittingPdfs = 'CompleteSplittingPdfs',
  CompleteIndexingMetadataExtraction = 'CompleteIndexingMetadataExtraction',
  CompleteAllSteps = 'CompleteAllSteps'
}

/** Display names where the enum's own words read badly (the metadata step is "Indexing Metadata"). */
const CHECKPOINT_NAMES: Partial<Record<string, string>> = {
  [ProcessingCheckpointEnum.CompleteIndexingMetadataExtraction]: 'Complete Indexing Metadata'
};

/** `CompleteIndexing` → "Complete Indexing"; with `short`, just the step: "Indexing". */
export function processingCheckpointLabel(value: string | null | undefined, short = false): string {
  if (!value) return '—';
  const name = CHECKPOINT_NAMES[value] ?? value.replace(/([a-z])([A-Z])/g, '$1 $2');
  return short ? name.replace(/^Complete /, '') : name;
}

export type CreateOrdersByStorageRequest = {
  name: string;
  storageId: number;
  storagePath: string;
  servicerLoanNumberRegex: string;
  scheduler: Scheduler;
  processingCheckpoint: ProcessingCheckpointEnum;
  encryptionId?: number | null;
  channelName?: string | null;
  reportsOutputDirectory?: string | null;
  scanBatchSize?: number | null;
  archivePath?: string | null;
};

export type EditOrdersRequest = {
  id: number;
  name: string;
  scheduler: Scheduler;
  processingCheckpoint: ProcessingCheckpointEnum;
  encryptionId?: number | null;
  channelName?: string | null;
  reportsOutputDirectory?: string | null;
  scanBatchSize?: number | null;
  archivePath?: string | null;
};

type BaseServicingTransferOrder = {
  id: number;
  name: string;
  createdDate: Date;
  lastUpdated: Date;
  loansCount: number;
  /** Source-file archiving on/off (the order has an archive path). Orders list only. */
  isArchiveEnabled?: boolean;
  encryptionId?: number | null;
  channelName?: string | null;
  storage?: string;
  storagePath?: string;
  servicerLoanNumberRegex?: string;
  scheduler: Scheduler;
  processingCheckpoint: ProcessingCheckpointEnum;
  reportsOutputDirectory?: string | null;
  scanBatchSize?: number | null;
  archivePath?: string | null;
};

export type ExportIndexingReportsRequest = {
  from: string;
  to: string;
  reportKeys?: string[];
  /** IANA id, e.g. "America/Chicago"; `from`/`to` carry its offset. */
  timeZone?: string;
};

/** 202: the reports are queued; their outcomes appear on `lastAttempt` in `getIndexingReports`. */
export type ExportIndexingReportsResponse = {
  orderId: number;
  status: 'Queued';
  /** Ready to show as-is. */
  message: string;
  from: string;
  to: string;
  reports: QueuedIndexingReport[];
};

export type QueuedIndexingReport = {
  /** Matches the report's `lastAttempt.exportId`. */
  exportId: number;
  reportKey: string;
  reportName: string;
};

/** A report CPM assigns to the order's channel, or one of CPM's default reports when the channel has none. */
export type IndexingReportSummary = {
  reportKey: string;
  cpmReportId: number;
  name: string;
  fileNamePattern: string;
  isSupported: boolean;
  lastAttempt: LastIndexingReportAttempt | null;
  lastGenerated: LastGeneratedReportFile | null;
  fromDefaultSet: boolean;
};

/** `Queued` while generating (`attemptedAtUtc` is then when it was queued); a lost run comes back `Failed`. */
export type IndexingReportAttemptStatus = 'Queued' | 'Succeeded' | 'Failed' | 'Unknown';

export type LastIndexingReportAttempt = {
  exportId: number;
  status: IndexingReportAttemptStatus;
  attemptedAtUtc: string;
  attemptedBy: string | null;
  fileName: string | null;
  error: string | null;
  from: string;
  to: string;
};

/** Status shown in the reports dialog; `generating` is a `Queued` attempt, or one being submitted. */
export type IndexingReportUiStatus = 'generated' | 'generating' | 'failed' | 'notGenerated';

export type LastGeneratedReportFile = {
  exportId: number;
  fileName: string;
  path: string;
  generatedAtUtc: string;
  generatedBy: string | null;
  rowCount: number;
  sizeBytes: number;
  contentType: string;
  from: string;
  to: string;
};

export enum Scheduler {
  None = 'None',
  Each5Minutes = 'Each5Minutes',
  Hourly = 'Hourly',
  TwiceADay = 'TwiceADay',
  Daily = 'Daily',
  Weekly = 'Weekly',
  Monthly = 'Monthly'
}

export interface ServicingTransferOrderDetail extends BaseServicingTransferOrder {
  outgoingServicer: string;
  status?: OrderStatusValue;
}

export type DataFile = {
  fileName: string;
  delimiter: DelimiterType;
  status: string;
  id?: number;
  orderDocumentId?: number;
  file?: File;
};

export type UploadDocument = {
  file: File;
  type: OrderDocumentType;
  delimiter?: DelimiterType;
};

export type MapFileColumn = {
  id: number;
  isSelected: boolean;
  column: string;
  sampleValue: string;
  confidenceScore?: number;
  entity?: string;
  attribute?: string;
};

export type CoreTablesSchema = {
  tableName: string;
  columnName: string;
};

export type CoreTablesSchemaMapped = {
  tableName: string;
  columns: string[];
};

export type DealImportResults = {
  mappedLoansCount: number;
  mappingErrors?: DealImportResultError[];
};

export type DealImportResultError = {
  isExpanded: boolean;
  loanNumber: number;
  hasErrors: boolean;
  unmappedColumns: {
    column: string;
    errorMessage: string;
  }[];
};

export type DefinitionColumn = {
  id: number;
  columnName: string;
  dataType: string;
  category: string;
  sampleData: string;
  definition: string;
};

export type ColumnMapping = {
  id: number;
  column?: string;
  entity?: string;
  attribute?: string;
  staticValue?: string;
  sampleData?: string;
};

export type ServicingTransferOrder = {
  id: number;
  name: string;
  createdDate: string;
  productType: string;
  dealName: string;
  outgoingServicer: string;
  status: string;
};

export type OrderDocument = {
  id: number;
  orderId: number;
  mappingTemplateId: number;
  errorDetails?: string;
  status: DocFileStatus;
  isGenAiSkipped: boolean;
  type: OrderDocumentType;
  name: string;
};

export type DocumentFileRow = {
  fileName: string;
  size: number;
  id?: number;
  orderDocumentId?: number;
  file?: File;
};

export type OnboardingFileRow = {
  fileName: string;
  size: number;
  status: string;
  errorDetails?: string;
  orderDocumentId: number;
  id?: number;
};

export type InvoiceFileRow = {
  fileName: string;
  size: number;
  loanDocumentId?: number;
  id?: number;
};

export type PayHistoryFileRow = {
  fileName: string;
  status: string;
  errorDetails?: string;
  orderDocumentId?: number;
  id?: number;
};

export type Column = {
  id: number;
  index: number;
  name: string;
  sampleData: string;
};

export type CreateMappingTemplateRequest = {
  name: string | null;
  mappingDirection: MappingDirection;
  columnMappings: ColumnMapping[];
};

export type ImportTemplate = {
  id: number;
  name: string;
};

export type ImportTemplateWithMappings = {
  id: number;
  name: string;
  mappingDirection: MappingDirection;
  columnMappings: ColumnMapping[];
};

export type ImportTemplateOptions = {
  newTemplateName?: string;
  updateTemplateId?: number;
};

export type Loan = {
  id: number;
  loanNumber?: string;
  servicerLoanNumber?: string;
  loanSubType?: string;
  insuranceCaseNumber?: string;
  assetType?: string;
  loanStatus?: string;
  nextPaymentDue?: string;
  lienPosition?: number;
  originationDate: string;
  firstPaymentDate?: string;
  originalBalance?: number;
  upb?: string;
  currentDeferredBalance?: number;
  escrowBalance?: number;
  corpBorrowerRecBalance?: number;
  corpThirdPartyRecBalance?: number;
  corpInvestorRecBalance?: number;
  corpNonRecBalance?: number;
  totalLegalBalance?: number;
  modificationDate?: string;
  b1FirstName?: string;
  b1MiddleName: null;
  b1LastName?: string;
  b1Type?: string;
  b2FirstName?: string;
  b2MiddleName: null;
  b2LastName?: string;
  b2Type?: string;
  propertyAddress?: string;
  propertyCity?: string;
  propertyState?: string;
  propertyZip?: string;
  propertyType?: string;
  occupancyType?: string;
  bkStatus?: string;
  bkChapter?: string;
  caseNumber?: string;
  petitionFiledDate?: string;
  fcStatus?: string;
  fcStage?: string;
  flaFiledDate?: string;
  saleHeldDate?: string;
  status: LoanStatus;
  originalDocumentCount: number;
};

export type LoanListItem = {
  id: number;
  servicerLoanNumber: number;
  loanNumber: string;
  originalDocumentCount: number;
  processingStatus: string;
};

export enum LoanStatus {
  New = 'New',
  PHReceived = 'PHReceived',
  PHStaged = 'PHStaged',
  InvoiceStaged = 'InvoiceStaged',
  Processing = 'Processing',
  Processed = 'Processed'
}

export enum ExtractionType {
  GOOGLEOCR = 'GoogleOcr',
  INDEXPDF = 'IndexPdf',
  EXTRACTDATA = 'ExtractData'
}

export enum DelimiterType {
  Comma = 'Comma',
  Pipe = 'Pipe',
  Tab = 'Tab',
  Excel = 'None'
}

export enum MappingDirection {
  ToEntity = 'ToEntity',
  ToFile = 'ToFile'
}

export enum OrderDocumentType {
  Unknown = 'Unknown',
  DocumentFile = 'DocumentFile',
  DataFile = 'DataFile',
  OnboardingFile = 'OnboardingFile',
  ServicerOnboardingFile = 'ServicerOnboardingFile',
  ExtractorOnboardingFile = 'ExtractorOnboardingFile',
  PayHistory = 'PayHistory',
  Invoice = 'Invoice',
  DocumentExtractionFile = 'DocumentExtractionFile'
}

export enum DocFileStatus {
  Unknown = 'Unknown',
  New = 'New',

  FetchedColumns = 'FetchedColumns',
  DDMProcessing = 'DDMProcessing',
  CMAProcessing = 'CMAProcessing',
  CMAProcessed = 'CMAProcessed',
  CMAFailed = 'CMAFailed',
  Mapping = 'Mapping',
  IngestingData = 'IngestingData',

  Completed = 'Completed',
  CompletedWithErrors = 'CompletedWithErrors',
  Failed = 'Failed'
}

export interface OrderLoanDocument {
  orderDocumentId: number;
  docNameOrig: string;
  loanId: string;
  loanNumber: number;
  size: string;
  numPages: number;
  status: string;
  docType: string;
  docNameNew: string;
  isExpanded: boolean;
}

export interface OrderArchive {
  id: number;
  parentDocumentId: number | null;
  name: string;
  /** File name as received (falls back to `name` for older documents). */
  originalName?: string | null;
  uuid?: string;
  loanId: number;
  loanNumber: string;
  size: number;
  numberOfPages: number;
  status: string;
  type: string;
  /** LoanDocuments.Id. Not shown in the UI (the Doc Id is `id`, Documents.Id); used only for reprocessing. */
  loanDocumentId: number;
  sha256Hash: string | null;
  isDuplicate: boolean;
  errorDetails: string | null;
  created: string;
  updated: string;
  isExpanded?: boolean;
  children: OrderArchive[];
}

/** A section's Blitz status, from its latest real attempt (first matching rule, in the API). */
export type SectionDeliveryStatus =
  | 'Sending'
  | 'ResendFailed'
  | 'SendFailed'
  | 'Rejected'
  | 'Confirmed'
  | 'AwaitingBlitz'
  | 'PreCutover'
  | 'Ready'
  | 'NotReady'
  /** The last send got no answer (or was cut off): Blitz may already have it. Only the admin Re-send sends it again. */
  | 'NoResponse'
  /** No document type, so no route sends it; it sends normally once it has one. */
  | 'Held';

/** One row of a section's attempt history. */
export interface DeliveryAttempt {
  attemptedAtUtc: string;
  source: string;
  outcome: string;
  responseCode?: number | null;
  transactionId?: string | null;
  blitzStatus: string;
  message?: string | null;
  isForcedOverride: boolean;
  /** A pre-cutover marker (Source 0), not a send. */
  isBackfill: boolean;
}

/** Why a section has no st.IndexingOutputResults row yet. */
export type NotReadyReason = 'NoDisplayName' | 'MetadataFailed' | 'OriginalFailedBeforeFinalization' | 'LoanStillProcessing';

export interface IndexedDocument {
  /** The section's Documents.Id: the Doc Id. */
  sectionDocumentId: number;
  /** The parent (original) document's Documents.Id. */
  documentId: number;
  documentName: string;
  loanId: number;
  loanNumber: string;
  category: string;
  type: string;
  subType: string;
  rtDocName: string;
  vendorDocumentName: string;
  startPage: number;
  endPage: number;
  numberOfPages: number;
  confidenceScore: number;
  loanDocumentId: number;
  documentDisplayName?: string | null;
  created?: string;
  updated?: string | null;
  indexingMetadataCompleted?: boolean;
  deliveryStatus: SectionDeliveryStatus;
  notReadyReason?: NotReadyReason | null;
  /** UTC. The latest send that got an answer. */
  sentAt?: string | null;
  /** UTC. When Blitz filed it (the API converts from US Central). */
  blitzUploadedOn?: string | null;
  blitzMessage?: string | null;
  /** Awaiting Blitz only: sent, but no answer was recorded, so it may not have arrived. */
  sendUnverified?: boolean;
  /** Real attempts only (no pre-cutover or Blocked rows). */
  attemptCount: number;
  transactionId?: string | null;
}

export interface ExtractedDocument {
  fieldId: number;
  /** Documents.Id: the Doc Id. */
  documentId: number;
  /** The original document the section was indexed from (Documents.ParentDocumentId). */
  parentDocumentId?: number | null;
  loanDocumentId: number;
  loanId: number;
  loanNumber?: string;
  name: string;
  value?: string;
  vendorDocumentName?: string;
  rtDocName?: string | null;
  created?: string;
  updated?: string | null;
}

export enum ProcessingStatus {
  New = 'New',
  Processing = 'Processing',
  Completed = 'Completed',
  Error = 'Error',
  Inactive = 'Inactive'
}

export const statusColorMap: Record<ProcessingStatus, string> = {
  [ProcessingStatus.New]: '#4A4A4A',
  [ProcessingStatus.Processing]: '#007BFF',
  [ProcessingStatus.Completed]: '#28A745',
  [ProcessingStatus.Error]: '#DC3545',
  [ProcessingStatus.Inactive]: '#CED4DA'
};

export enum DocumentType {
  Unknown = 'Unknown',
  Zip = 'Zip',
  Pdf = 'Pdf',
  Other = 'Other',
  Loan = 'Loan'
}

export enum DocumentAction {
  Download = 'Downloading',
  Unzip = 'Unzip',
  CopyToAzure = 'CopyToAzure',
  DuplicatesCheck = 'DuplicatesCheck',
  DocIndex = 'DocIndex',
  SplitPdfs = 'SplitPdfs',
  BestVersioning = 'BestVersioning',
  DocExtract = 'DocExtract',
  Encryption = 'Encryption'
}

export interface DocumentActivity {
  id: number;
  documentAction: DocumentAction;
  processingTimeInSeconds: number;
  errorDetails?: string | null;
  startTime?: string;
}

export interface Document {
  id: number;
  name: string;
  status: LoanDocumentStatus;
  processingStatus: ProcessingStatus;
  isError: boolean;
  type: DocumentType;
  processingTimeInSeconds: number;
  documentActivities: DocumentActivity[];
  childDocuments: Document[];
}

export interface DocumentColorLegend {
  color: string;
  label: string;
  value: string;
}

export interface LoanDocumentWithActivity {
  loanId: number;
  documents: Document[];
}
